/**
 * Responsive Navigation Component
 *
 * Layout Strategy:
 * - Mobile (<768px): Hamburger menu with slide-out drawer
 * - Tablet (768px-1023px): Condensed horizontal nav
 * - Desktop (1024px+): Full navigation bar
 */

import { Button } from '@nextui-org/react'
import React, { useEffect, useState } from 'react'
import { HiChartBar, HiCog, HiHome, HiLogout, HiVideoCamera } from 'react-icons/hi'
import { useLocation, useNavigate } from 'react-router-dom'
import logo from '../../assets/logo/FISHSCOPE_logo_black.png'
import '../../styles/responsive.css'

interface NavigationItem {
  path: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  mobileLabel?: string
  disabled?: boolean
}

const navigationItems: NavigationItem[] = [
  { path: '/', label: 'Home', icon: HiHome, mobileLabel: 'Home' },
  { path: '/chart', label: 'Chart', icon: HiChartBar, mobileLabel: 'Charts' },
  { path: '/live', label: 'Live', icon: HiVideoCamera, mobileLabel: 'Live', disabled: true },
]

interface ResponsiveNavigationProps {
  className?: string
  onLogout?: () => void
}

export const ResponsiveNavigation: React.FC<ResponsiveNavigationProps> = ({
  className = '',
  onLogout,
}) => {
  const [isMobile, setIsMobile] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()

  // Handle responsive breakpoint detection
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    checkIsMobile()
    window.addEventListener('resize', checkIsMobile)
    return () => window.removeEventListener('resize', checkIsMobile)
  }, [])

  const handleNavigation = (path: string) => {
    navigate(path)
  }

  // Mobile Navigation (< 768px)
  if (isMobile) {
    return (
      <>
        {/* Mobile Header Bar */}
        <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 mobile-header-height">
          <div className="flex items-center justify-center h-full px-4">
            {/* Logo/Brand - centered */}
            <div className="flex items-center">
              <img src={logo} alt="FishScope" className="h-8 w-auto object-contain mt-1" />
            </div>
          </div>
        </header>

        {/* Bottom Navigation */}
        {/* Backdrop to prevent click-through to content below */}
        <div
          className="fixed bottom-0 left-0 right-0 h-16 bg-transparent z-40"
          aria-hidden="true"
        />
        <nav className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 h-14 shadow-lg">
          <div className="flex items-center justify-around h-full px-2 bg-white relative">
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.path
              const isDisabled = item.disabled ?? false
              const Icon = item.icon

              return (
                <Button
                  key={item.path}
                  variant="ghost"
                  isDisabled={isDisabled}
                  className={`
                    touch-target flex-col h-full rounded-lg relative justify-center gap-0
                    ${isDisabled ? 'opacity-40 text-gray-400' : isActive ? 'text-tdplprussian' : 'text-gray-500'}
                  `}
                  onPress={() => handleNavigation(item.path)}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[0.625rem] font-light">
                    {item.mobileLabel || item.label}
                  </span>
                </Button>
              )
            })}
          </div>
        </nav>
      </>
    )
  }

  // Tablet Navigation (768px - 1023px)
  if (window.innerWidth < 1024) {
    return (
      <header
        className={`tablet-nav-container fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 ${className}`}
      >
        <div className="container-responsive">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img src={logo} alt="FishScope" className="h-10 w-auto" />
            </div>

            {/* Condensed Navigation */}
            <nav className="flex items-center space-x-4">
              {navigationItems.map((item) => {
                const isActive = location.pathname === item.path
                const isDisabled = item.disabled ?? false
                const Icon = item.icon

                return (
                  <Button
                    key={item.path}
                    variant={isActive ? 'flat' : 'ghost'}
                    isDisabled={isDisabled}
                    className={`
                      touch-target h-10 px-3
                      ${isDisabled ? 'opacity-40 text-gray-400' : isActive ? 'bg-tdplprussian/10 text-tdplprussian' : 'text-gray-700'}
                    `}
                    onPress={() => navigate(item.path)}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    <span className="hidden sm:inline text-sm">{item.label}</span>
                  </Button>
                )
              })}

              {/* Logout Button */}
              {onLogout && (
                <Button
                  variant="ghost"
                  className="touch-target h-10 px-3 text-red-600"
                  onPress={onLogout}
                >
                  <HiLogout className="w-4 h-4" />
                </Button>
              )}
            </nav>
          </div>
        </div>
      </header>
    )
  }

  // Desktop Navigation (1024px+)
  return (
    <header
      className={`desktop-nav-container fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200 ${className}`}
    >
      <div className="container-responsive">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex items-center">
            <img src={logo} alt="FishScope" className="h-12 w-auto" />
          </div>

          {/* Full Desktop Navigation */}
          <nav className="flex items-center space-x-6">
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.path
              const isDisabled = item.disabled ?? false
              const Icon = item.icon

              return (
                <Button
                  key={item.path}
                  variant={isActive ? 'flat' : 'ghost'}
                  isDisabled={isDisabled}
                  className={`
                    h-12 px-4
                    ${isDisabled ? 'opacity-40 text-gray-400' : isActive ? 'bg-tdplprussian/10 text-tdplprussian' : 'text-gray-700'}
                  `}
                  onPress={() => navigate(item.path)}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  <span className="font-medium">{item.label}</span>
                </Button>
              )
            })}

            {/* Settings */}
            <Button
              variant="ghost"
              className="h-12 px-4 text-gray-700"
              onPress={() => navigate('/settings')}
            >
              <HiCog className="w-5 h-5 mr-3" />
              <span className="font-medium">Settings</span>
            </Button>

            {/* Logout */}
            {onLogout && (
              <Button variant="ghost" className="h-12 px-4 text-red-600" onPress={onLogout}>
                <HiLogout className="w-5 h-5 mr-3" />
                <span className="font-medium">Logout</span>
              </Button>
            )}
          </nav>
        </div>
      </div>
    </header>
  )
}

export default ResponsiveNavigation
